import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/api';
import { vehicleName } from '../lib/utils';
import { Car, Search, Loader2, ChevronRight, Plus } from 'lucide-react';
import { Input } from '../components/ui/input';

function fmt(p) { return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(p); }
function calcMargin(v) {
    const vr = v.prix_vente + v.frais_circulation - v.remise_client;
    const mb = vr - v.prix_achat;
    const tva = mb > 0 ? mb * 20 / 120 : 0;
    let fHT = 0;
    (v.frais || []).forEach(f => { fHT += f.tva_recuperable ? f.montant_ttc / 1.20 : f.montant_ttc; });
    return mb - tva - fHT;
}

export default function MobileDossiers() {
    const navigate = useNavigate();
    const [vehicles, setVehicles] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        try {
            const res = await axios.get(`${API_URL}/api/vehicles`, { withCredentials: true });
            setVehicles(res.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const filtered = search
        ? vehicles.filter(v => {
            const s = search.toLowerCase();
            return [vehicleName(v), v.modele || '', v.immatriculation || ''].some(f => f.toLowerCase().includes(s));
        })
        : vehicles;

    if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

    return (
        <div className="px-5 pt-[calc(1rem+env(safe-area-inset-top))] pb-6" data-testid="mobile-dossiers">
            <div className="flex items-center justify-between mb-4">
                <h1 className="text-xl font-bold text-gray-900">Mes dossiers</h1>
                <button onClick={() => navigate('/calculator')}
                    className="w-9 h-9 rounded-xl bg-[#6366F1] flex items-center justify-center shadow-sm active:scale-95 transition-transform"
                    data-testid="mobile-dossiers-add">
                    <Plus className="w-5 h-5 text-white" strokeWidth={2.5} />
                </button>
            </div>

            {/* Search */}
            <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                    type="text"
                    placeholder="Rechercher un véhicule..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="pl-9 bg-white border-gray-200 rounded-xl h-10 text-[13px]"
                    data-testid="mobile-search"
                />
            </div>

            <p className="text-[12px] text-gray-400 mb-3">{filtered.length} dossier{filtered.length > 1 ? 's' : ''}</p>

            {filtered.length === 0 ? (
                <div className="text-center py-16">
                    <Car className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-400">{search ? 'Aucun résultat' : 'Aucun dossier'}</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {filtered.map(v => {
                        const m = calcMargin(v);
                        const statut = v.date_vente ? 'Vendu' : 'En stock';
                        const statutColor = v.date_vente ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700';
                        return (
                            <button key={v.id} onClick={() => navigate(`/calculator/${v.id}`)}
                                className="w-full bg-white rounded-xl p-3.5 flex items-center gap-3 shadow-sm border border-gray-100 active:scale-[0.98] transition-transform text-left"
                                data-testid={`mobile-dossier-${v.id}`}>
                                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
                                    <Car className="w-5 h-5 text-gray-400" strokeWidth={1.5} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-[13px] font-medium text-gray-900 truncate">{vehicleName(v)}</p>
                                    {v.immatriculation && <p className="text-[11px] text-gray-400 font-mono">{v.immatriculation}</p>}
                                </div>
                                <div className="text-right shrink-0">
                                    <p className={`text-[13px] font-mono font-semibold ${m >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(m)}</p>
                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-md ${statutColor}`}>{statut}</span>
                                </div>
                                <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
